import moment from 'moment';
import {
  NEW_SITE,
  SUMMARY,
  PAUSED_BY_EDITOR,
  PROBLEMS,
  SKIP_REASONS,
  WAITING_FOR_TECH,
} from './copy';

export const fromNow = value => (value ? moment(value).fromNow() : null);

export const exchangeCode = exchange =>
  exchange?.code || exchange?.stockExchangeName || '';

// "nairametrics.com", "www.nairametrics.com" or a full link all work.
export const normalizeAddress = input => {
  const text = (input || '').trim();
  if (!text) return null;
  try {
    const url = new URL(/^https?:\/\//i.test(text) ? text : `https://${text}`);
    return url.hostname.includes('.') ? url.href : null;
  } catch {
    return null;
  }
};

export const hostOf = url => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

// Read through another site's feed: the case where a section filter is a
// must (La Tribune Afrique read through latribune.fr).
export const readsOtherSitesFeed = (homepageUrl, feedUrl) =>
  Boolean(
    feedUrl && hostOf(feedUrl) && hostOf(feedUrl) !== hostOf(homepageUrl),
  );

export const nameFromHost = host => {
  const base = (host || '').split('.')[0] || host || '';
  return base.charAt(0).toUpperCase() + base.slice(1);
};

// ponytail: sorts the raw read error into the plan's kinds until news-service
// sends health.lastErrorKind; switch to that field once it ships.
export const errorKind = lastError => {
  const text = lastError || '';
  if (/robots/i.test(text)) return 'ROBOTS';
  if (/\b(401|403|429)\b/.test(text)) return 'BLOCKED';
  if (/\b(404|410)\b/.test(text)) return 'NOT_FOUND';
  return 'UNREACHABLE';
};

const QUARANTINE_AFTER = 5;

// One of five states, and when there is one, the sentence and button that
// explain it.
export const sourceStatus = source => {
  const health = source?.health || {};
  const failures = health.consecutiveFailures || 0;
  const kind = errorKind(health.lastError);

  if (source?.enabled === false) {
    if (!health.lastSuccessAt && !source?.discovery?.resolvedMode) {
      return {key: 'waiting', message: WAITING_FOR_TECH};
    }
    if (failures >= QUARANTINE_AFTER) {
      return {
        key: 'paused',
        message: PROBLEMS.QUARANTINED.message(PROBLEMS[kind].message),
        action: PROBLEMS.QUARANTINED.action,
      };
    }
    return {key: 'paused', message: PAUSED_BY_EDITOR};
  }
  if (failures > 0) {
    const problem = PROBLEMS[kind];
    return {
      key: kind === 'ROBOTS' ? 'paused' : 'trouble',
      message: problem.message,
      action: problem.action,
    };
  }
  if (!health.lastSuccessAt) return {key: 'new', message: NEW_SITE};
  if (
    !health.itemsLast7d &&
    source?.createdAt &&
    moment(source.createdAt).isBefore(moment().subtract(7, 'days'))
  ) {
    return {
      key: 'trouble',
      message: PROBLEMS.NO_ARTICLES.message,
      action: PROBLEMS.NO_ARTICLES.action,
    };
  }
  return {key: 'working'};
};

export const coverageFor = (source, exchangeId) =>
  (source?.coverage || []).find(row => row?.exchangeId === exchangeId);

// Reliability is written as 95 / 75 / 55, but seeded websites carry other
// values, so it is read by range.
export const TRUST = {official: 95, established: 75, smaller: 55};

export const reliabilityOf = trustWeight => {
  if (trustWeight === null || trustWeight === undefined) return 'established';
  if (trustWeight >= 90) return 'official';
  if (trustWeight >= 70) return 'established';
  return 'smaller';
};

export const coverOf = row => {
  if (row?.sectionFilter) return 'section';
  return row?.requireKeywords ? 'all' : 'market';
};

export const answersFromCoverage = (row, defaults) => ({
  cover: coverOf(row),
  keywords: row?.keywords?.length ? row.keywords : defaults?.keywords || [],
  section: row?.sectionFilter,
  reliability: reliabilityOf(row?.trustWeight),
});

// The coverage fields the answers set. Reliability is only written when the
// editor changed it, so a seeded 85 is not quietly rewritten to 75.
export const coverageFromAnswers = (answers, previous) => {
  const fields = {
    keywords: answers.keywords,
    requireKeywords: answers.cover === 'all',
    sectionFilter: answers.cover === 'section' ? answers.section : undefined,
  };
  if (
    !previous ||
    reliabilityOf(previous.trustWeight) !== answers.reliability
  ) {
    fields.trustWeight = TRUST[answers.reliability];
  }
  return fields;
};

// The first part of each sample's path, with how many samples sit under it.
// A bare number is a date folder, not a section.
export const sectionOptions = samples => {
  const counts = {};
  (samples || []).forEach(sample => {
    try {
      const [first, ...rest] = new URL(sample.link).pathname
        .split('/')
        .filter(Boolean);
      if (!first || !rest.length || /^\d+$/.test(first)) return;
      const section = `/${first}/`;
      counts[section] = (counts[section] || 0) + 1;
    } catch {
      // A sample without a usable link has no section.
    }
  });
  return Object.entries(counts)
    .map(([value, count]) => ({value, count}))
    .sort((a, b) => b.count - a.count);
};

const fold = text =>
  (text || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

// The words found in a text. A word inside a longer one that was also found
// counts once: "stock" inside "stock market" is one mention, not two.
export const keywordsIn = (text, keywords) => {
  const folded = fold(text);
  const found = keywords
    .map(fold)
    .filter((word, i, all) => word && all.indexOf(word) === i)
    .filter(word => folded.includes(word));
  return found.filter(
    word => !found.some(other => other !== word && other.includes(word)),
  );
};

// ponytail: an in-browser estimate of the routing rules (the keyword gate is
// news-service's passesKeywordGate). It sees only the sample's excerpt, not the
// full text ingestion reads. Swap for previewNewsSourceRouting once it ships,
// so the preview can't disagree with what ingestion does.
export const previewSample = (sample, answers) => {
  if (answers.cover === 'section') {
    return answers.section && (sample.link || '').includes(answers.section)
      ? {used: true}
      : {used: false, reason: SKIP_REASONS.section};
  }
  if (answers.cover === 'all') {
    // Kept when the headline has one word, or the text has two different ones.
    if (keywordsIn(sample.title, answers.keywords).length) return {used: true};
    const inText = keywordsIn(
      `${sample.title} ${sample.excerpt}`,
      answers.keywords,
    );
    if (inText.length >= 2) return {used: true};
    return {
      used: false,
      reason: inText.length ? SKIP_REASONS.oneKeyword : SKIP_REASONS.keyword,
    };
  }
  return {used: true};
};

// ponytail: defaults read from the websites the exchange already has. Replace
// with news-service's newsExchangeDefaults once it ships.
const LOCALE_BY_EXCHANGE = {BRVM: 'FR:fr', NGX: 'NG:en'};

export const exchangeDefaults = (exchange, sources = []) => {
  const rows = sources
    .flatMap(source => source?.coverage || [])
    .filter(row => row?.exchangeId === exchange?.id);
  const keywords = rows
    .filter(row => row.requireKeywords && row.keywords?.length)
    .map(row => row.keywords)
    .sort((a, b) => b.length - a.length)[0];
  const gnewsLocale =
    LOCALE_BY_EXCHANGE[exchange?.code] ||
    rows.find(row => row.gnewsLocale)?.gnewsLocale ||
    'US:en';
  return {
    keywords: keywords || [],
    gnewsLocale,
    language: gnewsLocale.endsWith(':fr') ? 'FR' : 'EN',
  };
};

// "NGX, stocks or shares"; past three words, "NGX, stocks, shares or 4 more words".
export const wordList = words => {
  if (!words?.length) return '';
  if (words.length === 1) return words[0];
  const shown = words.slice(0, 3);
  const rest = words.length - shown.length;
  if (rest > 0) return `${shown.join(', ')} or ${rest} more words`;
  return `${shown.slice(0, -1).join(', ')} or ${shown[shown.length - 1]}`;
};

const COVERAGE_KEYS = [
  'exchangeId',
  'exchangeCode',
  'exchangeName',
  'enabled',
  'sectionFilter',
  'gnewsLocale',
  'keywords',
  'trustWeight',
  'defaultTag',
  'defaultType',
  'defaultCategories',
  'requireKeywords',
  'autoPublish',
];

// Query results carry __typename and nulls; the input type accepts neither.
export const toCoverageInput = row =>
  COVERAGE_KEYS.reduce((input, key) => {
    if (row?.[key] !== undefined && row?.[key] !== null) input[key] = row[key];
    return input;
  }, {});

// updateNewsSource replaces the whole coverage list, so one row's change is
// sent together with every other row, unchanged. A key set to undefined in
// `changes` clears it.
export const replaceCoverage = (source, exchangeId, changes) =>
  (source?.coverage || []).map(row =>
    toCoverageInput(row.exchangeId === exchangeId ? {...row, ...changes} : row),
  );

export const otherExchanges = (source, exchangeId) =>
  (source?.coverage || [])
    .filter(row => row.exchangeId !== exchangeId)
    .map(row => row.exchangeCode || row.exchangeName);

// The Step 3 summary: what saving will do, in words.
export const summarySentence = (name, exchange, answers) =>
  [
    SUMMARY.feeds(name, exchange),
    answers.cover === 'market' && SUMMARY.market,
    answers.cover === 'all' && SUMMARY.keywords(wordList(answers.keywords)),
    answers.cover === 'section' && SUMMARY.section(answers.section),
    SUMMARY[answers.reliability],
  ]
    .filter(Boolean)
    .join(' ');
